<template>
  <div class="test-report-list">
    <div class="page-header">
      <h2>自动化测试报告</h2>
      <div class="header-actions">
        <el-button type="primary" icon="el-icon-cpu" @click="handleExecuteTest">
          自动化测试
        </el-button>
        <el-button icon="el-icon-refresh" @click="fetchReportList">刷新</el-button>
      </div>
    </div>

    <!-- 统计摘要 -->
    <div class="stats-summary" v-if="statsData">
      <el-row :gutter="20">
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-icon"><i class="el-icon-document"></i></div>
            <div class="stat-content">
              <div class="stat-value">{{ statsData.total_reports }}</div>
              <div class="stat-label">总报告数</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-icon success"><i class="el-icon-circle-check"></i></div>
            <div class="stat-content">
              <div class="stat-value">{{ statsData.total_success }}</div>
              <div class="stat-label">成功次数</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-icon fail"><i class="el-icon-circle-close"></i></div>
            <div class="stat-content">
              <div class="stat-value">{{ statsData.total_fail }}</div>
              <div class="stat-label">失败次数</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-icon warning"><i class="el-icon-timer"></i></div>
            <div class="stat-content">
              <div class="stat-value">{{ statsData.total_slow }}</div>
              <div class="stat-label">慢接口数</div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 搜索表单 -->
    <el-form :inline="true" :model="searchForm" class="search-form">
      <el-form-item label="报告名称">
        <el-input v-model="searchForm.report_name" placeholder="请输入报告名称" clearable />
      </el-form-item>
      <el-form-item label="执行状态">
        <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
          <el-option label="执行中" value="running" />
          <el-option label="已完成" value="completed" />
          <el-option label="失败" value="failed" />
        </el-select>
      </el-form-item>
      <el-form-item label="创建时间">
        <el-date-picker
          v-model="searchForm.dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          clearable />
      </el-form-item>
      <el-form-item label="创建人">
        <el-input v-model="searchForm.creator" placeholder="请输入创建人" clearable />
      </el-form-item>
      <el-form-item label="成功率">
        <el-slider
          v-model="searchForm.successRateRange"
          range
          :min="0"
          :max="100"
          :step="5"
          show-stops
          :format-tooltip="formatSuccessRate"
          style="width: 200px" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 数据表格 -->
    <el-table v-loading="loading" :data="reportList" border style="width: 100%">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="report_no" label="报告编号" width="200" />
      <el-table-column prop="report_name" label="报告名称" min-width="180" show-overflow-tooltip />
      <el-table-column prop="total_count" label="总测试数" width="100" align="center" />
      <el-table-column prop="success_count" label="成功数" width="100" align="center">
        <template slot-scope="scope">
          <span class="success-text">{{ scope.row.success_count }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="fail_count" label="失败数" width="100" align="center">
        <template slot-scope="scope">
          <span v-if="scope.row.fail_count > 0" class="fail-text">{{ scope.row.fail_count }}</span>
          <span v-else>{{ scope.row.fail_count }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="success_rate" label="成功率" width="120" align="center">
        <template slot-scope="scope">
          <el-progress 
            :percentage="Number(scope.row.success_rate)" 
            :color="getProgressColor(Number(scope.row.success_rate))"
            :format="formatPercentage" />
        </template>
      </el-table-column>
      <el-table-column prop="execute_time" label="耗时(秒)" width="100" align="center">
        <template slot-scope="scope">
          {{ scope.row.execute_time }}s
        </template>
      </el-table-column>
      <el-table-column prop="login_status" label="登录状态" width="100" align="center">
        <template slot-scope="scope">
          <el-tag :type="scope.row.login_status === 1 ? 'success' : 'danger'" size="small">
            {{ scope.row.login_status === 1 ? '成功' : '失败' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="执行状态" width="100" align="center">
        <template slot-scope="scope">
          <el-tag :type="getStatusType(scope.row.status)" size="small">
            {{ scope.row.status_text }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="creator" label="执行人" width="100" />
      <el-table-column prop="create_time" label="创建时间" width="160" />
      <el-table-column label="操作" width="180" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" type="text" @click="handleView(scope.row)">查看</el-button>
          <el-button size="mini" type="text" class="delete-btn" @click="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.limit"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 执行测试对话框 -->
    <el-dialog title="执行自动化测试" :visible.sync="executeDialogVisible" width="500px">
      <div class="execute-dialog-content">
        <el-alert
          title="自动化测试说明"
          type="info"
          :closable="false"
          show-icon>
          <template>
            <p>1. 系统将先执行登录接口，获取Token</p>
            <p>2. 然后依次执行所有已启用的普通接口</p>
            <p>3. 测试结果将生成详细报告</p>
          </template>
        </el-alert>
        <div class="execute-tip" v-if="executeLoading">
          <i class="el-icon-loading"></i>
          <span>正在执行自动化测试，请稍候...</span>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="executeDialogVisible = false" :disabled="executeLoading">关 闭</el-button>
        <el-button type="primary" @click="confirmExecuteTest" :disabled="executeLoading">
          {{ executeLoading ? '执行中...' : '开始测试' }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 测试进度对话框 -->
    <el-dialog title="测试执行进度" :visible.sync="pollingDialogVisible" width="700px" :close-on-click-modal="false">
      <div class="polling-dialog-content">
        <div v-if="pollingData" class="progress-section">
          <div class="overall-progress">
            <div class="progress-label">总体进度</div>
            <el-progress
              :percentage="calculateTotalProgress()"
              :color="getProgressColor(calculateTotalProgress())"
              :format="formatPercentage" />
          </div>
          <div class="progress-stats">
            <el-row :gutter="20">
              <el-col :span="6">
                <div class="stat-item">
                  <div class="stat-label">总任务数</div>
                  <div class="stat-value">{{ pollingData.total_count || 0 }}</div>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="stat-item">
                  <div class="stat-label">成功数</div>
                  <div class="stat-value success">{{ pollingData.success_count || 0 }}</div>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="stat-item">
                  <div class="stat-label">失败数</div>
                  <div class="stat-value fail">{{ pollingData.fail_count || 0 }}</div>
                </div>
              </el-col>
              <el-col :span="6">
                <div class="stat-item">
                  <div class="stat-label">成功率</div>
                  <div class="stat-value">{{ pollingData.success_rate || 0 }}%</div>
                </div>
              </el-col>
            </el-row>
          </div>
          <div class="module-progress" v-if="pollingData.module_progress">
            <div class="progress-label">模块进度</div>
            <div v-for="(progress, module) in pollingData.module_progress" :key="module" class="module-item">
              <span class="module-name">{{ module }}</span>
              <el-progress
                :percentage="progress.progress || 0"
                :color="getProgressColor(progress.progress)"
                :format="(p) => progress.completed + '/' + progress.total"
                style="width: 300px" />
            </div>
          </div>
        </div>
        <div v-else class="loading-state">
          <i class="el-icon-loading"></i>
          <span>正在启动测试任务...</span>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="closePollingDialog">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 报告详情对话框 -->
    <el-dialog title="测试报告详情" :visible.sync="detailDialogVisible" width="1200px" top="5vh">
      <div v-if="currentReport" class="report-detail">
        <!-- 报告概要 -->
        <div class="report-summary">
          <el-row :gutter="20">
            <el-col :span="6">
              <div class="summary-item">
                <div class="summary-label">报告编号</div>
                <div class="summary-value">{{ currentReport.report_no }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-item">
                <div class="summary-label">执行状态</div>
                <div class="summary-value">
                  <el-tag :type="getStatusType(currentReport.status)">{{ currentReport.status_text }}</el-tag>
                </div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-item">
                <div class="summary-label">执行耗时</div>
                <div class="summary-value">{{ currentReport.execute_time }}s</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-item">
                <div class="summary-label">执行时间</div>
                <div class="summary-value">{{ currentReport.create_time }}</div>
              </div>
            </el-col>
          </el-row>
          <el-row :gutter="20" style="margin-top: 20px;">
            <el-col :span="6">
              <div class="summary-item">
                <div class="summary-label">总测试数</div>
                <div class="summary-value big">{{ currentReport.total_count }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-item">
                <div class="summary-label">成功数</div>
                <div class="summary-value big success">{{ currentReport.success_count }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-item">
                <div class="summary-label">失败数</div>
                <div class="summary-value big fail">{{ currentReport.fail_count }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-item">
                <div class="summary-label">慢接口</div>
                <div class="summary-value big warning">{{ currentReport.slow_count || 0 }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-item">
                <div class="summary-label">成功率</div>
                <div class="summary-value big">{{ currentReport.success_rate }}%</div>
              </div>
            </el-col>
          </el-row>
        </div>

        <!-- 登录信息 -->
        <div class="detail-section">
          <div class="section-title">登录信息</div>
          <div class="login-info">
            <el-tag :type="currentReport.login_status === 1 ? 'success' : 'danger'" size="small">
              {{ currentReport.login_status === 1 ? '登录成功' : '登录失败' }}
            </el-tag>
            <span v-if="currentReport.login_status === 1 && currentReport.login_token" class="token-display">
              Token: {{ maskToken(currentReport.login_token) }}
            </span>
          </div>
        </div>

        <!-- 接口测试结果 - 树形表格 -->
        <div class="detail-section">
          <div class="section-title">
            <i class="el-icon-s-data"></i> 接口测试结果
            <span class="result-summary" v-if="currentReport">
              <span class="success-count">✓ {{ currentReport.success_count }} 成功</span>
              <span class="fail-count">✗ {{ currentReport.fail_count }} 失败</span>
            </span>
          </div>
          <el-table
            :data="treeTableData"
            border
            size="small"
            max-height="600"
            row-key="uniqueKey"
            :row-class-name="getRowClassName"
            :tree-props="{children: 'children', hasChildren: 'hasChildren'}">
            <el-table-column prop="api_name" label="接口名称" min-width="220">
              <template slot-scope="scope">
                <div :class="['api-name-cell', {'is-login': scope.row.is_login, 'is-module': !scope.row.is_leaf && !scope.row.sub_module, 'is-sub-module': !scope.row.is_leaf && scope.row.sub_module}]">
                  <i v-if="scope.row.is_login" class="el-icon-key key-icon"></i>
                  <i v-else-if="!scope.row.is_leaf && scope.row.sub_module" class="el-icon-folder-opened folder-icon"></i>
                  <i v-else-if="!scope.row.is_leaf" class="el-icon-menu module-icon"></i>
                  <span>{{ scope.row.api_name }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="test_type_text" label="测试类型" width="90" align="center">
              <template slot-scope="scope">
                <el-tag v-if="scope.row.test_type === 'need_data'" type="warning" size="small">依赖型</el-tag>
                <el-tag v-else-if="scope.row.test_type === 'independent'" type="primary" size="small">独立型</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="sub_module" label="子模块" width="130" align="center">
              <template slot-scope="scope">
                <span v-if="scope.row.sub_module" class="sub-module-text">{{ scope.row.sub_module }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="api_url" label="接口地址" min-width="220" show-overflow-tooltip />
            <el-table-column prop="api_method" label="方法" width="70" align="center">
              <template slot-scope="scope">
                <el-tag v-if="scope.row.api_method" :type="getMethodType(scope.row.api_method)" size="small">
                  {{ scope.row.api_method }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="response_status" label="状态码" width="100" align="center">
              <template slot-scope="scope">
                <template v-if="scope.row.response_status">
                  <el-tag :type="scope.row.response_status === scope.row.expected_status ? 'success' : 'danger'" size="small">
                    {{ scope.row.response_status }}
                  </el-tag>
                  <span class="expected-status">/{{ scope.row.expected_status }}</span>
                </template>
                <span v-else class="muted">-</span>
              </template>
            </el-table-column>
            <el-table-column prop="response_time" label="耗时" width="90" align="center">
              <template slot-scope="scope">
                <template v-if="scope.row.response_time">
                  <span :class="scope.row.is_slow === 1 ? 'slow-text' : ''">
                    {{ scope.row.response_time }}s
                  </span>
                  <el-tag v-if="scope.row.is_slow === 1" type="warning" size="mini">慢</el-tag>
                </template>
                <span v-else class="muted">-</span>
              </template>
            </el-table-column>
            <el-table-column prop="is_success_final" label="结果" width="120" align="center">
              <template slot-scope="scope">
                <template v-if="scope.row.is_leaf && scope.row.has_test_result !== false">
                  <el-tag v-if="scope.row.is_expected_fail" type="warning" size="small">
                    ✓ 预期失败
                  </el-tag>
                  <el-tag v-else-if="scope.row.is_success_final === 1" type="success" size="small">
                    ✓ 成功
                  </el-tag>
                  <el-tag v-else type="danger" size="small">
                    ✗ 失败
                  </el-tag>
                  <!-- 回滚标记 -->
                  <el-tag v-if="scope.row.is_rolled_back === 1" type="info" size="small" style="margin-left: 4px">
                    ↩回滚
                  </el-tag>
                </template>
                <el-tag v-else-if="scope.row.is_leaf" type="info" size="small">未测</el-tag>
                <el-tag v-else-if="scope.row.children && scope.row.children.length > 0"
                  :type="scope.row.is_success === 1 ? 'success' : 'danger'" size="small">
                  {{ scope.row.is_success === 1 ? '✓ 成功' : '✗ 失败' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="error_message" label="错误信息" min-width="200" show-overflow-tooltip />
            <el-table-column label="操作" width="80" fixed="right">
              <template slot-scope="scope">
                <el-button
                  v-if="scope.row.is_leaf && scope.row.api_id"
                  size="mini"
                  type="text"
                  @click="viewApiDetail(scope.row)">
                  详情
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="closeDetailDialog">关 闭</el-button>
      </div>
    </el-dialog>

    <!-- 接口详情对话框 -->
    <el-dialog title="接口请求详情" :visible.sync="apiDetailVisible" width="900px">
      <div v-if="currentApiDetail" class="api-detail-content">
        <el-collapse v-model="activeCollapse">
          <el-collapse-item title="请求信息" name="request">
            <div class="detail-row">
              <span class="detail-label">接口名称：</span>
              <span class="detail-value">{{ currentApiDetail.api_name }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">请求地址：</span>
              <span class="detail-value">{{ currentApiDetail.domain }}{{ currentApiDetail.api_url }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">请求方法：</span>
              <el-tag :type="getMethodType(currentApiDetail.api_method)" size="small">
                {{ currentApiDetail.api_method }}
              </el-tag>
            </div>
            <div class="detail-row">
              <span class="detail-label">请求头：</span>
              <pre class="json-content">{{ formatJson(currentApiDetail.request_headers) }}</pre>
            </div>
            <div class="detail-row">
              <span class="detail-label">请求参数：</span>
              <pre class="json-content">{{ formatJson(currentApiDetail.request_params) }}</pre>
            </div>
            <div class="detail-row">
              <span class="detail-label">请求体：</span>
              <pre class="json-content">{{ formatJson(currentApiDetail.request_body) }}</pre>
            </div>
          </el-collapse-item>
          <el-collapse-item title="响应信息" name="response">
            <div class="detail-row">
              <span class="detail-label">响应状态：</span>
              <span :class="currentApiDetail.response_status === currentApiDetail.expected_status ? 'success-text' : 'fail-text'">
                {{ currentApiDetail.response_status }}
              </span>
              <span class="expected-status">（预期：{{ currentApiDetail.expected_status }}）</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">响应耗时：</span>
              <span>{{ currentApiDetail.response_time }}s</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">错误信息：</span>
              <span v-if="currentApiDetail.error_message" class="fail-text">{{ currentApiDetail.error_message }}</span>
              <span v-else>-</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">响应内容：</span>
              <pre class="json-content response-content">{{ formatJson(currentApiDetail.response_content) }}</pre>
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="apiDetailVisible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  executeTest,
  getReportList,
  getReportDetail,
  deleteReport,
  getTestProgress
} from '@/api/test'

export default {
  name: 'TestReportList',
  data() {
    return {
      loading: false,
      reportList: [],
      statsData: null,
      searchForm: {
        report_name: '',
        status: '',
        dateRange: [],
        creator: '',
        successRateRange: [0, 100]
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0
      },
      executeDialogVisible: false,
      executeLoading: false,
      pollingDialogVisible: false,
      pollingLoading: false,
      pollingTimer: null,
      pollingReportId: null,
      pollingData: null,
      detailDialogVisible: false,
      apiDetailVisible: false,
      currentReport: null,
      currentApiDetail: null,
      activeCollapse: ['request', 'response'],
      treeTableData: []
    }
  },
  mounted() {
    this.fetchReportList()
  },
  methods: {
    // 获取报告列表
    async fetchReportList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
          report_name: this.searchForm.report_name,
          status: this.searchForm.status,
          start_date: this.searchForm.dateRange && this.searchForm.dateRange[0] ? this.searchForm.dateRange[0] : '',
          end_date: this.searchForm.dateRange && this.searchForm.dateRange[1] ? this.searchForm.dateRange[1] : '',
          creator: this.searchForm.creator,
          success_rate_min: this.searchForm.successRateRange ? this.searchForm.successRateRange[0] : 0,
          success_rate_max: this.searchForm.successRateRange ? this.searchForm.successRateRange[1] : 100
        }
        const response = await getReportList(params)
        this.reportList = response.data.list || []
        this.pagination.total = response.data.total || 0

        // 计算统计数据
        this.calculateStats()
      } catch (error) {
        console.error('获取报告列表失败:', error)
      } finally {
        this.loading = false
      }
    },

    // 计算统计数据
    calculateStats() {
      const reports = this.reportList || []
      if (reports.length === 0) {
        this.statsData = null
        return
      }

      const totalReports = reports.length
      const totalSuccess = reports.filter(r => r.status === 'completed' && r.success_rate == 100).length
      const totalFail = reports.filter(r => r.status === 'completed' && r.fail_count > 0).length
      const runningCount = reports.filter(r => r.status === 'running').length
      const totalSlow = reports.reduce((sum, r) => sum + (r.slow_count || 0), 0)

      // 计算平均成功率（只统计已完成的）
      const completedReports = reports.filter(r => r.status === 'completed')
      const avgSuccessRate = completedReports.length > 0
        ? Math.round(completedReports.reduce((sum, r) => sum + (r.success_rate || 0), 0) / completedReports.length)
        : 0

      this.statsData = {
        total_reports: totalReports,
        total_success: totalSuccess + totalFail > 0 ? totalSuccess : 0,
        total_fail: totalFail,
        total_slow: totalSlow,
        avg_success_rate: avgSuccessRate,
        running_count: runningCount
      }
    },

    handleSearch() {
      this.pagination.page = 1
      this.fetchReportList()
    },

    handleReset() {
      this.searchForm = {
        report_name: '',
        status: '',
        dateRange: [],
        creator: '',
        successRateRange: [0, 100]
      }
      this.pagination.page = 1
      this.fetchReportList()
    },

    handleSizeChange(size) {
      this.pagination.limit = size
      this.fetchReportList()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchReportList()
    },

    // 执行测试
    handleExecuteTest() {
      this.executeDialogVisible = true
    },

    async confirmExecuteTest() {
      this.executeLoading = true
      try {
        const result = await executeTest({})
        this.executeDialogVisible = false
        this.pollingReportId = result.data.report_id
        this.pollingData = null
        this.pollingDialogVisible = true
        this.pollingLoading = true
        this.startPolling()
      } catch (error) {
        this.$message.error(error.message || '执行失败')
        this.executeLoading = false
      }
    },

    // 开始轮询进度
    startPolling() {
      this.stopPolling()
      this.pollingTimer = setInterval(() => {
        this.pollTestProgress()
      }, 2000)
    },

    // 停止轮询
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
      }
    },

    // 轮询获取测试进度
    async pollTestProgress() {
      if (!this.pollingReportId) return
      try {
        // 优先使用新的实时进度API
        const response = await getTestProgress(this.pollingReportId)
        if (response.code === 200 && response.data) {
          this.pollingData = this.formatProgressData(response.data)
        } else {
          // 降级到旧的poll_test API
          const fallback = await this.$http.get('/admin/test_controller/poll_test', {
            params: { report_id: this.pollingReportId }
          })
          this.pollingData = fallback.data
        }
        // 检查是否完成
        if (this.pollingData.is_completed || this.pollingData.status === 'completed' || this.pollingData.status === 'failed') {
          this.stopPolling()
          this.pollingLoading = false
          this.$message.success('测试执行完成')
        }
      } catch (error) {
        console.error('轮询失败:', error)
      }
    },

    // 格式化进度数据
    formatProgressData(data) {
      const total = data.total || 0
      const completed = data.completed || 0
      return {
        report_id: data.report_id,
        status: data.status,
        total_count: total,
        success_count: data.success || 0,
        fail_count: data.fail || 0,
        success_rate: total > 0 ? Math.round((data.success / total) * 100, 2) : 0,
        is_completed: data.status !== 'running',
        module_progress: data.modules || {}
      }
    },

    // 计算总进度
    calculateTotalProgress() {
      if (!this.pollingData || !this.pollingData.module_progress) return 0
      let total = 0
      let completed = 0
      Object.values(this.pollingData.module_progress).forEach(progress => {
        total += progress.total || 0
        completed += progress.completed || 0
      })
      return total > 0 ? Math.round((completed / total) * 100) : 0
    },

    // 关闭轮询对话框
    closePollingDialog() {
      this.stopPolling()
      this.pollingDialogVisible = false
      this.pollingReportId = null
      this.pollingData = null
      this.fetchReportList()
    },

    // 查看报告详情
    async handleView(row) {
      try {
        const response = await getReportDetail(row.id)
        this.currentReport = response.data
        // 构建树形数据
        this.buildTreeTableData(response.data.details || [])
        this.detailDialogVisible = true
      } catch (error) {
        this.$message.error('获取报告详情失败')
        console.error('获取报告详情失败:', error)
      }
    },

    // 构建树形表格数据
    buildTreeTableData(details) {
      if (!details || details.length === 0) {
        this.treeTableData = []
        return
      }
      // 后端已返回带 children 的树形结构，直接使用
      this.treeTableData = details
    },

    // 关闭详情弹窗时清空树形数据
    closeDetailDialog() {
      this.detailDialogVisible = false
      this.treeTableData = []
      this.currentReport = null
    },

    // 查看接口详情
    viewApiDetail(row) {
      this.currentApiDetail = row
      this.apiDetailVisible = true
    },

    // 删除报告
    handleDelete(row) {
      this.$confirm('确认删除该测试报告吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await deleteReport(row.id)
          this.$message.success('删除成功')
          this.fetchReportList()
        } catch (error) {
          console.error('删除失败:', error)
        }
      }).catch(() => {})
    },

    // 获取状态类型
    getStatusType(status) {
      const typeMap = {
        'running': 'warning',
        'completed': 'success',
        'failed': 'danger'
      }
      return typeMap[status] || 'info'
    },

    // 获取方法类型
    getMethodType(method) {
      const typeMap = {
        'GET': 'success',
        'POST': 'primary',
        'PUT': 'warning',
        'DELETE': 'danger',
        'PATCH': 'info'
      }
      return typeMap[method] || 'info'
    },

    // 获取进度条颜色
    getProgressColor(rate) {
      if (rate >= 80) return '#67C23A'
      if (rate >= 60) return '#E6A23C'
      return '#F56C6C'
    },

    // 格式化百分比
    formatPercentage(percentage) {
      return percentage + '%'
    },

    // 格式化成功率
    formatSuccessRate(val) {
      return val + '%'
    },

    // 格式化Token（脱敏）
    maskToken(token) {
      if (!token || token.length < 15) return token
      return token.substring(0, 8) + '...' + token.substring(token.length - 8)
    },

    // 获取行样式类名
    getRowClassName({row}) {
      if (row.is_login) return 'row-login'
      if (!row.is_leaf && !row.sub_module) return 'row-module'
      if (!row.is_leaf) return 'row-sub-module'
      return ''
    },

    // 格式化JSON
    formatJson(data) {
      if (!data) return '-'
      try {
        const parsed = typeof data === 'string' ? JSON.parse(data) : data
        return JSON.stringify(parsed, null, 2)
      } catch (error) {
        return data
      }
    }
  }
}
</script>

<style scoped>
.test-report-list {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 24px;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.search-form {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.success-text {
  color: #67C23A;
  font-weight: bold;
}

.fail-text {
  color: #F56C6C;
  font-weight: bold;
}

.execute-dialog-content {
  padding: 10px 0;
}

.execute-tip {
  margin-top: 20px;
  padding: 15px;
  background: #f0f9eb;
  border-radius: 4px;
  text-align: center;
  color: #67C23A;
}

.execute-tip i {
  margin-right: 8px;
}

.report-detail {
  padding: 10px 0;
}

.report-summary {
  background: #f5f7fa;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.summary-item {
  text-align: center;
}

.summary-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 8px;
}

.summary-value {
  font-size: 16px;
  color: #303133;
}

.summary-value.big {
  font-size: 28px;
  font-weight: bold;
}

.summary-value.big.success {
  color: #67C23A;
}

.summary-value.big.fail {
  color: #F56C6C;
}

.summary-value.big.warning {
  color: #E6A23C;
}

.slow-text {
  color: #E6A23C;
  font-weight: bold;
}

.detail-section {
  margin-bottom: 30px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 15px;
  padding-left: 10px;
  border-left: 4px solid #409EFF;
  line-height: 20px;
  display: flex;
  align-items: center;
}

.section-title i {
  margin-right: 8px;
  font-size: 18px;
  color: #409EFF;
}

.result-summary {
  margin-left: 20px;
  font-size: 14px;
  font-weight: normal;
}

.result-summary .success-count {
  color: #67C23A;
  margin-right: 15px;
}

.result-summary .fail-count {
  color: #F56C6C;
}

.muted {
  color: #C0C4CC;
}

/* 接口名称单元格 */
.api-name-cell {
  display: flex;
  align-items: center;
}

.api-name-cell.is-login {
  color: #E6A23C;
  font-weight: 500;
}

.api-name-cell.is-module {
  color: #409EFF;
  font-weight: 600;
}

.api-name-cell.is-sub-module {
  color: #606266;
  font-weight: 500;
}

.key-icon {
  margin-right: 6px;
  color: #E6A23C;
}

.folder-icon {
  margin-right: 6px;
  color: #909399;
}

.module-icon {
  margin-right: 6px;
  color: #409EFF;
}

.sub-module-text {
  padding: 2px 8px;
  background: #f0f2f5;
  border-radius: 4px;
  font-size: 12px;
}

/* 树形表格行样式 */
.el-table >>> .row-module {
  background-color: #ecf5ff !important;
}

.el-table >>> .row-sub-module {
  background-color: #fafafa !important;
}

.el-table >>> .row-login {
  background-color: #fffbf0 !important;
}

/* 状态码样式 */
.expected-status {
  color: #C0C4CC;
  font-size: 11px;
  margin-left: 2px;
}

.login-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.token-display {
  font-family: monospace;
  font-size: 12px;
  color: #606266;
  word-break: break-all;
}

.api-detail-content {
  padding: 10px 0;
}

.detail-row {
  display: flex;
  margin-bottom: 15px;
  align-items: flex-start;
}

.detail-label {
  font-weight: 500;
  color: #606266;
  width: 100px;
  flex-shrink: 0;
}

.detail-value {
  color: #303133;
  word-break: break-word;
}

.json-content {
  background: #f5f7fa;
  padding: 10px;
  border-radius: 4px;
  font-size: 12px;
  margin: 5px 0 0 0;
  max-height: 300px;
  overflow: auto;
  flex: 1;
}

.response-content {
  background: #fff5f5;
}

.expected-status {
  color: #909399;
  font-size: 12px;
  margin-left: 5px;
}

.polling-dialog-content {
  padding: 10px 0;
}

.progress-section {
  padding: 10px 0;
}

.overall-progress {
  margin-bottom: 20px;
}

.progress-label {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
}

.progress-stats {
  margin-bottom: 20px;
}

.stat-item {
  text-align: center;
}

.stat-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 5px;
}

.stat-value {
  font-size: 20px;
  font-weight: bold;
  color: #303133;
}

.stat-value.success {
  color: #67C23A;
}

.stat-value.fail {
  color: #F56C6C;
}

.module-progress {
  margin-top: 20px;
}

.module-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.module-name {
  width: 80px;
  font-size: 13px;
  color: #606266;
}

.loading-state {
  text-align: center;
  padding: 40px 0;
  color: #909399;
}

.loading-state i {
  font-size: 24px;
  margin-right: 10px;
}

.stats-summary {
  background: #f5f7fa;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.stat-card {
  display: flex;
  align-items: center;
  background: #fff;
  padding: 15px 20px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #409EFF;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.stat-icon i {
  font-size: 24px;
  color: #fff;
}

.stat-icon.success {
  background: #67C23A;
}

.stat-icon.fail {
  background: #F56C6C;
}

.stat-icon.rate {
  background: #E6A23C;
}

.stat-icon.warning {
  background: #E6A23C;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
}

.stat-label {
  font-size: 13px;
  color: #909399;
  margin-top: 5px;
}
</style>
